import { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Search, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const filterRows = (items, q, toRow) => {
  const s = (q || "").trim().toLowerCase();
  if (!s) return items;
  return items.filter((it) => toRow(it).map((c) => String(c ?? "")).join(" ").toLowerCase().includes(s));
};

export function useTableSearch(items, toRow) {
  const [q, setQ] = useState("");
  return [q, setQ, filterRows(items || [], q, toRow)];
}

export function exportCSV({ title, filename, cols, rows }) {
  const esc = (c) => { const v = String(c ?? ""); return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v; };
  const lines = [[title], [], cols, ...rows];
  const csv = lines.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export function exportPDF({ title, filename, cols, rows }) {
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(15); doc.setTextColor(15, 118, 110);
  doc.text(title, 14, 16);
  doc.setFontSize(10); doc.setTextColor(100);
  doc.text(`${rows.length} rows`, 14, 22);
  autoTable(doc, {
    startY: 28, head: [cols], body: rows.map((r) => r.map((c) => String(c ?? ""))),
    headStyles: { fillColor: [15, 118, 110] }, styles: { fontSize: 6.5, cellPadding: 1.2 },
  });
  doc.save(`${filename}.pdf`);
}

// Search + CSV + PDF toolbar. `rows` = all rows matching the current search/filters (not just one page).
export function TableTools({ id, query, onQuery, cols, rows, title, filename, children }) {
  const meta = { title, filename, cols, rows };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${id}-toolbar`}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid={`${id}-search`} value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search…" className="h-9 w-48 pl-8 bg-white border-slate-300 text-slate-900 text-sm" />
      </div>
      {children}
      <Button variant="outline" size="sm" onClick={() => exportCSV(meta)} data-testid={`${id}-export-csv`} className="gap-2 h-9"><Download className="w-4 h-4" /> CSV</Button>
      <Button variant="outline" size="sm" onClick={() => exportPDF(meta)} data-testid={`${id}-export-pdf`} className="gap-2 h-9"><FileText className="w-4 h-4" /> PDF</Button>
    </div>
  );
}
